import React, { useMemo, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { ADMIN_TRADE_HISTORY_START } from '../../utils/adminTradeHistoryWindow';
import { ADMIN_TRACKED_LABEL } from '../../utils/adminTradeSymbols';
import { jtncMonthlyPnl } from '../../utils/qfaaTradierPnl';
import { formatMoney, pnlClass } from './adminMoney';
import {
  EXCLUSION_LABEL,
  isRulesDeployPending,
  jtncUnavailableHint,
  jtncUnavailableTitle,
} from './jtncLedgerText';

export { formatMoney, pnlClass };

const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const CATEGORY_FILTERS = [
  { id: 'all', label: 'All events' },
  { id: 'trade', label: 'Fills' },
  { id: 'expiration', label: 'Expirations' },
  { id: 'credit', label: 'Credits / bonuses' },
  { id: 'subscription', label: 'Pro subscriptions' },
  { id: 'fee', label: 'Other fees (not JTNC)' },
  { id: 'deposit', label: 'Deposits / withdrawals' },
  { id: 'income', label: 'Interest / dividends' },
  { id: 'other', label: 'Other' },
];

const CATEGORY_LABEL = {
  trade: 'Fill',
  expiration: 'Expired',
  credit: 'Credit / bonus',
  subscription: 'Pro subscription',
  fee: 'Fee',
  deposit: 'Deposit',
  income: 'Interest / dividend',
  other: 'Other',
};

const sideOf = (e) => {
  if (e.quantity == null || e.quantity === 0) return '';
  return e.quantity > 0 ? 'BUY' : 'SELL';
};

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const AdminTradierHistoryPanel = ({ userId, clientName, history, jtncLedger, jtncInputsError }) => {
  const [month, setMonth] = useState('all');
  const [showExcluded, setShowExcluded] = useState(false);
  const [category, setCategory] = useState('all');
  const state = history;
  const load = history.reload;

  const { data } = state;
  const summary = data?.summary;

  const events = useMemo(() => {
    if (!data?.events) return [];
    return data.events.filter(
      (e) =>
        (month === 'all' || e.date.startsWith(month)) &&
        (category === 'all' || e.category === category)
    );
  }, [data, month, category]);

  const monthRow = useMemo(
    () => (month === 'all' ? null : summary?.months.find((m) => m.month === month) || null),
    [summary, month]
  );

  const jtncMonths = useMemo(() => (jtncLedger ? jtncMonthlyPnl(jtncLedger) : null), [jtncLedger]);
  const excludedRows = useMemo(
    () => (jtncLedger?.excluded || [])
      .filter((e) => month === 'all' || e.date.startsWith(month))
      .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [jtncLedger, month]
  );

  const exportCsv = () => {
    const head = ['Date', 'Category', 'Type', 'Symbol', 'Underlying', 'Side', 'Quantity', 'Price', 'Commission', 'Amount', 'Description'];
    const rows = events.map((e) => [
      e.date, CATEGORY_LABEL[e.category] || e.category, e.type, e.symbol, e.underlying, sideOf(e),
      e.quantity == null ? '' : Math.abs(e.quantity), e.price, e.commission, e.amount, e.description,
    ]);
    const blob = new Blob(['\ufeff' + [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tradier_${(clientName || userId).replace(/[^\w.-]+/g, '_')}_${month === 'all' ? 'all' : month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (state.loading && !data) {
    return (
      <div className="p-10 text-center text-gray-400 text-sm">
        <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
        Loading Tradier history since{' '}
        {ADMIN_TRADE_HISTORY_START.toLocaleString('en-US', { month: 'long', year: 'numeric' })}…
        <div className="text-xs text-gray-500 mt-1">Large accounts can take up to a minute.</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="m-4 p-4 rounded-lg bg-red-950/30 border border-red-700/40 text-sm text-red-200 space-y-2">
        <div>{state.error}</div>
        {state.accountId && <div className="text-xs text-red-300/80">Tradier account {state.accountId}</div>}
        {state.code === 'NEEDS_REAUTH' && (
          <div className="text-xs text-red-300/80">
            Tradier history is only available while the client&apos;s Tradier connection is active.
          </div>
        )}
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 hover:bg-gray-700"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const totals = monthRow || summary;
  const jtncOf = (m) => {
    const j = jtncMonths?.[m] || { pnl: 0, trades: 0, reimbursements: 0, excluded: 0 };
    return { trades: j.trades + j.reimbursements, excluded: j.excluded, pnl: j.pnl };
  };
  const jtncTotals = !jtncLedger
    ? null
    : monthRow
      ? jtncOf(month)
      : {
        trades: jtncLedger.tradePnl + jtncLedger.reimbursementPnl,
        excluded: jtncLedger.excluded.reduce((s, e) => s + e.amount, 0),
        pnl: jtncLedger.total,
      };
  const cards = [
    { label: 'JTNC Trade P&L', value: jtncTotals?.pnl, pending: !jtncTotals },
    { label: 'JTNC trades', value: jtncTotals?.trades, pending: !jtncTotals },
    { label: 'Left out (Echo / test / personal)', value: jtncTotals?.excluded, pending: !jtncTotals },
    { label: 'Credits / bonuses', value: totals.credits },
    { label: 'Pro subscriptions', value: totals.subscriptions },
    monthRow
      ? { label: 'Fills / expired', text: `${monthRow.fills} / ${monthRow.expirations}` }
      : { label: 'Net deposits', value: summary.netDeposits, unsigned: true },
  ];

  return (
    <div className="space-y-3 p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-400">
          Tradier account <span className="font-mono text-gray-300">{data.accountId}</span> · {data.start} → {data.end}
          {' · '}fills: {ADMIN_TRACKED_LABEL} only
          {data.excludedEvents > 0 && ` (${data.excludedEvents} other-symbol events hidden)`}
        </span>
        <div className="flex-1" />
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 focus:outline-none focus:border-purple-500"
        >
          <option value="all">All months</option>
          {summary.months.map((m) => (
            <option key={m.month} value={m.month}>{monthLabel(m.month)}</option>
          ))}
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 focus:outline-none focus:border-purple-500"
        >
          {CATEGORY_FILTERS.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={exportCsv}
          disabled={!events.length}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" /> CSV
        </button>
        <button
          type="button"
          onClick={load}
          disabled={state.loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-40"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${state.loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {data.truncated && (
        <div className="p-3 rounded-lg bg-amber-950/40 border border-amber-600/40 text-amber-200 text-xs">
          Tradier returned more events than this view pages through. Totals may be incomplete.
        </div>
      )}

      {jtncInputsError && !isRulesDeployPending(jtncInputsError) && (
        <div className="p-3 rounded-lg bg-amber-950/40 border border-amber-600/40 text-amber-200 text-xs">
          Desk trade list / reimbursements could not be loaded ({jtncInputsError}), so JTNC figures are not shown.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-2 text-xs">
        {cards.map((c) => (
          <div key={c.label} className="bg-gray-800 rounded-lg px-3 py-2" title={c.pending && jtncInputsError ? jtncUnavailableTitle(jtncInputsError) : undefined}>
            <div className="text-gray-500">{c.label}</div>
            <div className={`font-semibold tabular-nums ${c.text || c.unsigned || c.pending ? 'text-gray-200' : pnlClass(c.value)}`}>
              {c.pending ? (jtncInputsError ? '—' : '…') : c.text ?? formatMoney(c.value, { signed: !c.unsigned })}
            </div>
            {c.pending && jtncInputsError && (
              <div className="text-[10px] mt-0.5 text-amber-400/80">{jtncUnavailableHint(jtncInputsError)}</div>
            )}
          </div>
        ))}
      </div>

      {month === 'all' && summary.months.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full text-xs">
            <thead className="bg-gray-900 text-left text-gray-400 uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 font-medium">Month</th>
                {['JTNC Trade P&L', 'JTNC trades', 'Left out'].map((h) => (
                  <th key={h} className="px-3 py-2 font-medium text-right">
                    {h}
                    {jtncInputsError && (
                      <div className="normal-case tracking-normal text-[10px] font-normal text-amber-400/80">
                        {jtncUnavailableHint(jtncInputsError)}
                      </div>
                    )}
                  </th>
                ))}
                <th className="px-3 py-2 font-medium text-right">Credits / bonuses</th>
                <th className="px-3 py-2 font-medium text-right">Pro subscriptions</th>
                <th className="px-3 py-2 font-medium text-right">Fills</th>
                <th className="px-3 py-2 font-medium text-right">Expired</th>
              </tr>
            </thead>
            <tbody>
              {summary.months.map((m) => {
                const j = jtncLedger ? jtncOf(m.month) : null;
                const none = jtncInputsError ? '—' : '…';
                return (
                <tr
                  key={m.month}
                  onClick={() => setMonth(m.month)}
                  className="border-t border-gray-800/80 hover:bg-gray-800/40 cursor-pointer"
                >
                  <td className="px-3 py-1.5">{monthLabel(m.month)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${j ? pnlClass(j.pnl) : 'text-gray-400'}`}>{j ? formatMoney(j.pnl) : none}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${j ? pnlClass(j.trades) : 'text-gray-400'}`}>{j ? formatMoney(j.trades) : none}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-gray-400">{j ? formatMoney(j.excluded) : none}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${pnlClass(m.credits)}`}>{formatMoney(m.credits)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${pnlClass(m.subscriptions)}`}>{formatMoney(m.subscriptions)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{m.fills}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{m.expirations}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {excludedRows.length > 0 && (
        <div className="rounded-lg border border-gray-800">
          <button
            type="button"
            onClick={() => setShowExcluded((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-300 hover:bg-gray-800/40"
          >
            <span>
              Left out of JTNC P&amp;L{month === 'all' ? '' : ` · ${monthLabel(month)}`}: {excludedRows.length} fills
              {' · '}
              {Object.entries(EXCLUSION_LABEL)
                .map(([k, label]) => [label, excludedRows.filter((e) => e.reason === k)])
                .filter(([, rows]) => rows.length)
                .map(([label, rows]) => `${label} ${formatMoney(rows.reduce((s, e) => s + e.amount, 0))}`)
                .join(' · ')}
            </span>
            <span className="text-gray-500">{showExcluded ? 'Hide' : 'Show'}</span>
          </button>
          {showExcluded && (
            <div className="overflow-x-auto max-h-80 border-t border-gray-800">
              <table className="w-full text-xs">
                <thead className="bg-gray-900 text-left text-gray-400 uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Contract</th>
                    <th className="px-3 py-2 font-medium">Side</th>
                    <th className="px-3 py-2 font-medium text-right">Qty</th>
                    <th className="px-3 py-2 font-medium text-right">Amount</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {excludedRows.map((e, i) => (
                    <tr key={`${e.date}-${e.symbol}-${i}`} className="border-t border-gray-800/80">
                      <td className="px-3 py-1.5 tabular-nums">{e.date}</td>
                      <td className="px-3 py-1.5 font-mono">{e.symbol}</td>
                      <td className="px-3 py-1.5">{e.category === 'expiration' ? 'EXPIRED' : sideOf(e)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{e.quantity == null ? '' : Math.abs(e.quantity)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${pnlClass(e.amount)}`}>{formatMoney(e.amount)}</td>
                      <td className="px-3 py-1.5 text-gray-400">{EXCLUSION_LABEL[e.reason] || e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {events.length === 0 ? (
        <div className="p-6 text-center text-gray-500 text-sm">No Tradier events for this filter.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-left text-xs text-gray-400 uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Event</th>
                <th className="px-3 py-2 font-medium">Symbol</th>
                <th className="px-3 py-2 font-medium">Side</th>
                <th className="px-3 py-2 font-medium text-right">Qty</th>
                <th className="px-3 py-2 font-medium text-right">Price</th>
                <th className="px-3 py-2 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={`${e.date}-${e.type}-${e.symbol}-${i}`} className="border-t border-gray-800/80 hover:bg-gray-800/40">
                  <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-300">{e.date}</td>
                  <td className="px-3 py-1.5 text-xs text-gray-400">{CATEGORY_LABEL[e.category] || e.type}</td>
                  <td className="px-3 py-1.5 text-xs">
                    <div className="font-medium">{e.symbol || e.description || '—'}</div>
                    {e.symbol && e.description && <div className="text-gray-500">{e.description}</div>}
                  </td>
                  <td className="px-3 py-1.5 text-xs">{sideOf(e)}</td>
                  <td className="px-3 py-1.5 text-xs text-right tabular-nums">
                    {e.quantity ? Math.abs(e.quantity) : ''}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right tabular-nums">{e.price ?? ''}</td>
                  <td className={`px-3 py-1.5 text-right font-medium tabular-nums ${pnlClass(e.amount)}`}>
                    {formatMoney(e.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminTradierHistoryPanel;
